import type { Directive } from 'vue';

import { watch } from 'vue';

import { permissionCheckRef } from '~/store/auth/usePermissionCheck';

type StopHandle = () => void;

const originalDisplayByElement = new WeakMap<HTMLElement, string>();

const stopHandleByElement = new WeakMap<HTMLElement, StopHandle>();

function applyVisibility(
  el: HTMLElement,
  roles: string[],
  check: (_requiredRoles: string[]) => boolean,
) {
  const hasAccess = check(roles);

  if (hasAccess) {
    const original = originalDisplayByElement.get(el);
    /* eslint-disable no-param-reassign -- directive must control element visibility */
    el.style.display = original ?? '';
    /* eslint-enable no-param-reassign */
    if (original !== undefined) {
      originalDisplayByElement.delete(el);
    }
  } else {
    if (!originalDisplayByElement.has(el)) {
      originalDisplayByElement.set(el, el.style.display || getComputedStyle(el).display);
    }
    /* eslint-disable no-param-reassign -- directive must control element visibility */
    el.style.display = 'none';
    /* eslint-enable no-param-reassign */
  }
}

export const vPermission: Directive<HTMLElement, string[]> = {

  mounted(el, binding) {
    const update = () => {
      const roles = Array.isArray(binding.value) ? binding.value : [binding.value];
      const check = permissionCheckRef.value;
      applyVisibility(el, roles, check);
    };

    const stopWatch = watch(
      [permissionCheckRef, () => binding.value],
      update,
      { immediate: true },
    );

    stopHandleByElement.set(el, stopWatch);
  },

  unmounted(el) {
    stopHandleByElement.get(el)?.();
    stopHandleByElement.delete(el);
  },

  updated(el, binding) {
    const roles = Array.isArray(binding.value) ? binding.value : [binding.value];
    applyVisibility(el, roles, permissionCheckRef.value);
  },
};

export default vPermission;
